import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    File02Icon,
    LibraryIcon,
    Login03Icon,
    Logout03Icon,
    User03Icon,
    UserSettings01Icon,
} from '@hugeicons-pro/core-bulk-rounded'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import {
    Avatar,
    Box,
    Button,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { useGetContactQuery, useGetCustomerQuery } from '@/app/services/customer'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import TbxToolbar from '@/common/components/TbxToolbar/TbxToolbar'
import TbxToolbarActions from '@/common/components/TbxToolbar/TbxToolbarActions'
import TbxToolbarDivider from '@/common/components/TbxToolbar/TbxToolbarDivider'
import TbxToolbarTitle from '@/common/components/TbxToolbar/TbxToolbarTitle'
import { useSignIn } from '@/common/hooks/useSignIn'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { Paths } from '@/common/utils'

import LanguageSelectorButton from '../LanguageSelectorButton/LanguageSelectorButton'
import TbxDialog from '../TbxDialog/TbxDialog'
import TbxToolbarLogo from '../TbxToolbar/TbxToolbarLogo'

import AppLogo from './AppLogo'

const classes = {
    appBarLogo: {
        height: 'var(--store-logo-size, 32px)',
        width: 'auto',
        boxSizing: 'content-box',
        cursor: 'pointer',
        flex: 0,
    },

    appBarTitle: {
        fontWeight: 500,
        fontSize: '18px',
        lineHeight: '21px',
        textTransform: 'uppercase',
        color: 'text.primary',
    },
}

const AppToolbar = ({ children = null }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const theme = useTheme()

    const { showAccountSettings, showOrderHistory } = useToolBoxTreatments()

    const { isAuthenticated, user } = useKindeAuth()
    const signIn = useSignIn()

    const logoPosition = theme.components?.MuiAppBar?.styleOverrides?.root?.['--store-logo-position'] || 'left'
    const isCenter = logoPosition === 'center'
    const isRight = logoPosition === 'right'

    const toolbarSx = {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
    }

    const logoSx = {
        gridColumn: isCenter ? 2 : isRight ? 3 : 1,
        gridRow: 1,
        justifySelf: isCenter ? 'center' : isRight ? 'end' : 'start',
        ...(isRight && { flexDirection: 'row-reverse' }),
    }

    const actionsSx = {
        gridColumn: isRight ? 1 : 3,
        gridRow: 1,
        justifySelf: isRight ? 'start' : 'end',
        ...(isRight && { flexDirection: 'row-reverse' }),
    }

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const paidFeatures = useSelector(selectPaidFeatures)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const currentGuest = useSelector(selectGuestUser)

    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [loginClicked, setLoginClicked] = useState(false)

    const { data: currentCustomer } = useGetCustomerQuery(
        {
            organisationId,
            customerId: currentCustomerId,
        },
        { skip: !currentCustomerId }
    )

    const { data: currentContact } = useGetContactQuery(
        {
            organisationId,
            customerId: currentCustomerId,
            contactId: currentContactId,
        },
        { skip: !currentContactId || !currentCustomerId }
    )

    const open = Boolean(anchorEl)

    const handleLogoutDialogOpen = () => setIsLogoutDialogOpen(true)

    const handleIsLogoutDialogClose = () => setIsLogoutDialogOpen(false)

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const handleLogOut = () => {
        setAnchorEl(null)
        handleIsLogoutDialogClose()
        navigate(Paths.LOGOUT_PATHNAME)
    }

    const handleLogIn = () => {
        setLoginClicked(true)
        signIn()
    }

    return (
        <TbxToolbar sx={toolbarSx}>
            <TbxToolbarLogo sx={logoSx}>
                <AppLogo
                    logoUrl={storeSettings?.webStoreLogoUri}
                    sx={classes.appBarLogo}
                    onClick={() => {
                        navigate(Paths.HOME_PATHNAME)
                    }}
                />

                {storeSettings?.webStoreLogoUri ? <TbxToolbarDivider sx={{ marginInline: '12px' }} /> : null}

                <TbxToolbarTitle sx={classes.appBarTitle}>
                    {storeSettings?.webStoreDisplayName || storeSettings?.organisationName}
                </TbxToolbarTitle>
            </TbxToolbarLogo>

            <TbxToolbarActions sx={actionsSx}>
                <LanguageSelectorButton />

                {children}

                <TbxToolbarDivider />

                <Box className="profileIcon">
                    {isAuthenticated || isGuestLoggedIn ? (
                        <>
                            <IconButton
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                aria-label="account of current user"
                                color="primary"
                                size="small"
                                onClick={handleMenu}
                            >
                                {isAuthenticated ? (
                                    <Avatar
                                        alt={currentContact?.name}
                                        sx={{ bgcolor: 'secondary.main' }}
                                    >
                                        {user.givenName.charAt(0).toUpperCase() +
                                            user.familyName.charAt(0).toUpperCase()}
                                    </Avatar>
                                ) : (
                                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                        <HugeiconsIcon
                                            color="white"
                                            icon={User03Icon}
                                        />
                                    </Avatar>
                                )}
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                id="menu-appbar"
                                open={open}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                onClose={handleCloseMenu}
                            >
                                <ListItem
                                    key="profile"
                                    divider
                                >
                                    <ListItemAvatar>
                                        {isAuthenticated ? (
                                            <Avatar
                                                alt={currentContact?.name}
                                                sx={{ bgcolor: 'secondary.main' }}
                                            >
                                                {user.givenName.charAt(0).toUpperCase() +
                                                    user.familyName.charAt(0).toUpperCase()}
                                            </Avatar>
                                        ) : (
                                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                                <HugeiconsIcon
                                                    color="white"
                                                    icon={User03Icon}
                                                />
                                            </Avatar>
                                        )}
                                    </ListItemAvatar>
                                    {isAuthenticated && currentContactId && currentCustomerId ? (
                                        <ListItemText
                                            primary={currentContact.name}
                                            secondary={currentCustomer?.companyName || ''}
                                        />
                                    ) : (
                                        <ListItemText
                                            primary={currentGuest?.name}
                                            secondary={t('Guest user')}
                                        />
                                    )}
                                </ListItem>
                                <TbxToolbarDivider />

                                {isAuthenticated && currentCustomerId
                                    ? [
                                          showOrderHistory && (
                                              <MenuItem
                                                  component={Link}
                                                  key="orders"
                                                  to="/my-orders"
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={File02Icon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My orders')} />
                                              </MenuItem>
                                          ),
                                          storeSettings?.webStorePartLibraryEnabled && paidFeatures?.hasPartLibrary && (
                                              <MenuItem
                                                  component={Link}
                                                  key="parts"
                                                  to="/my-parts"
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={LibraryIcon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My parts')} />
                                              </MenuItem>
                                          ),
                                          showAccountSettings && (
                                              <MenuItem
                                                  component={Link}
                                                  key="account"
                                                  to="/account"
                                                  divider
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={UserSettings01Icon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My account')} />
                                              </MenuItem>
                                          ),
                                      ]
                                    : null}
                                <MenuItem
                                    key="logout"
                                    onClick={handleLogoutDialogOpen}
                                >
                                    <ListItemIcon sx={{ color: 'text.primary' }}>
                                        <HugeiconsIcon icon={Logout03Icon} />
                                    </ListItemIcon>
                                    <ListItemText primary={t('Log out')} />
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <Button
                            aria-label="login"
                            color="primary"
                            loading={loginClicked}
                            loadingPosition="start"
                            size="small"
                            startIcon={<HugeiconsIcon icon={Login03Icon} />}
                            onClick={handleLogIn}
                        >
                            {t('Log in')}
                        </Button>
                    )}
                </Box>
            </TbxToolbarActions>

            <TbxDialog
                closeButtonText={t('cancel')}
                confirmButtonText={t('Yes, Log out')}
                content={t('Are you sure you want to log out? You will be navigated away from the current order.')}
                handleClose={handleIsLogoutDialogClose}
                handleConfirmClose={handleLogOut}
                isOpen={isLogoutDialogOpen}
                title={t('Log out')}
                uniqueKey={currentContactId || 'NO_CONTACT'}
            />
        </TbxToolbar>
    )
}

export default AppToolbar
